/**
 * Derive avatar initials from an email local-part until a full name is loaded
 * from the individual profile. Pure. e.g. "startup.admin@x.dev" -> "SA".
 */
/**
 * Derive a friendly display name from an email local-part until a full name is
 * loaded from the individual profile. Pure. e.g. "sarah.chen@x.dev" -> "Sarah Chen".
 */
export function displayNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[.\-_]+/).filter(Boolean);
  if (parts.length === 0) return email;
  return parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[.\-_]+/).filter(Boolean);
  // Use the cleaned parts (never the raw local-part) so a leading/trailing
  // separator does not leak a punctuation char as the initial.
  const source = parts.length >= 2 ? [parts[0], parts[1]] : [parts[0] ?? local];
  const letters = source.map((p) => p.charAt(0)).join('');
  return letters.toUpperCase().slice(0, 2) || 'U';
}

/**
 * Two-letter avatar initials from a full name (e.g. "Maya Lopez" -> "ML"), for
 * someone who has actually provided a name (a named invitee, a named share
 * recipient) rather than falling back to email-derived initials. Pure.
 */
export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.map((part) => part.charAt(0).toUpperCase()).join('');
  return letters.slice(0, 2) || 'U';
}

/**
 * The one avatar-initials rule for the signed-in user: from their name once the
 * profile has loaded, from the email until then. Shared by the header bubble
 * and the profile pages so they never disagree (UX sheet: the header showed
 * email-derived "SA"/"P" while the profile showed the name's "PT"). Pure.
 */
export function userInitials(
  profile: { firstName: string; lastName: string } | undefined,
  email: string | undefined,
): string {
  const name = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '';
  if (name) return initialsFromName(name);
  return email ? initialsFromEmail(email) : 'U';
}

/**
 * Splits a single "Full Name" form field back into the `firstName`/`lastName`
 * pair the individual-account API stores (FR-09). The account edit form
 * (Figma node 176:471) shows one Full Name input; the first word is the first
 * name, everything after is the last name - matching how the view page joins
 * them back together with a single space.
 */
export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim().replace(/\s+/g, ' ');
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) return { firstName: trimmed, lastName: '' };
  return {
    firstName: trimmed.slice(0, spaceIndex),
    lastName: trimmed.slice(spaceIndex + 1),
  };
}
